import { Capacitor } from '@capacitor/core';

/**
 * The app in the App Store and Google Play is a shell that loads this website (see
 * mobil/README.md). Everything that only applies there sits behind `isNativeApp`, so the
 * browser never notices the difference.
 */
export function isNativeApp(): boolean {
  return Capacitor.isNativePlatform();
}

/** The app id in both stores. Must match capacitor.config.ts. */
export const NATIVE_APP_ID = 'com.nordicluxadvisor.app';

/**
 * The paths the app accepts links to. Must match src/app/.well-known/* and
 * android/app/src/main/AndroidManifest.xml.
 */
export const APP_LINK_PATHS = [
  '/bookings',
  '/booking-confirmation',
  '/event/tickets/order',
  '/hotels',
  '/initiatives',
  '/experience',
];

/**
 * The path of a link the app was opened with, or null when the link is not ours.
 *
 * The link comes from outside (an email, another app), so it is parsed as a URL and accepted
 * only when it points at our own origin and a path we asked for. Without that, a foreign link
 * could send the app anywhere.
 */
export function pathFromAppLink(link: string): string | null {
  let url: URL;
  try {
    url = new URL(link);
  } catch {
    return null;
  }
  if (url.origin !== window.location.origin) return null;
  const known = APP_LINK_PATHS.some((p) => url.pathname === p || url.pathname.startsWith(`${p}/`));
  return known ? `${url.pathname}${url.search}${url.hash}` : null;
}

export const APP_LOGIN = '/auth/login';

/** The marketing site. The app never shows it: a signed-in member lands on the front page. */
const MARKETING_PAGES = ['/about', '/services', '/contact', '/experience', '/membership'];

/** What a visitor may see in the app without signing in. */
const SIGNED_OUT_PAGES = ['/auth', '/privacy', '/terms', '/delete-account', '/unsubscribe', '/avmeld'];

function underAny(pathname: string, prefixes: string[]): boolean {
  return prefixes.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/**
 * Where the app sends the user instead of `pathname`, or null when the page is fine.
 *
 * The app opens on the login page and shows only the pages behind it. A visitor who is not
 * signed in is sent to the login, with the page they wanted as `redirect_url` so a link from
 * an email still ends up where it pointed. A signed-in member who reaches a marketing page
 * (from the footer, a notification, a stale link) is sent to the front page.
 */
export function appRedirectFor(pathname: string, signedIn: boolean): string | null {
  if (!signedIn) {
    if (underAny(pathname, SIGNED_OUT_PAGES)) return null;
    if (pathname === '/' || underAny(pathname, MARKETING_PAGES)) return APP_LOGIN;
    return `${APP_LOGIN}?redirect_url=${encodeURIComponent(pathname)}`;
  }
  // The membership page is the one marketing page a signed-in free member needs: the site
  // sends them there until they have a membership.
  if (underAny(pathname, MARKETING_PAGES) && !underAny(pathname, ['/membership'])) return '/';
  return null;
}

/** Where to go after signing out: the login in the app, the front page in a browser. */
export function signedOutHome(): string {
  return isNativeApp() ? APP_LOGIN : '/';
}
